import { newId } from './ids';
import { V2_SCHEMA_VERSION } from './inspection';
import type { ResponsibleTrade } from './responsibleTrade';

// Finding domain — an OTHER FINDING of an area: an issue the inspector records outside the checklist
// (「+ 发现其他问题」), or a voluntary observation (OD-13). Since the Final Owner Decision of 2026-09-14
// (ratification CA-15 / D-CA-1 / D-CA-3) a checklist row carries NO finding record: a Defect on a row
// is the inspector's result on that row, and the report's defect entry for it is derived from the
// row (its report sentence, its photos, the rules library's professional) at assembly time.
export type FindingOrigin = 'inspector' | 'ai' | 'system';

// Single classification authority (OWNER-CONTRACT §8.7 / Frozen Spec §34). "normal" is not a
// finding. No severity field: severity would only be a compatibility shadow of the class.
export type FindingClass = 'defect' | 'safety' | 'maintenance' | 'advisory' | 'informational';

export type FindingStatus = 'draft' | 'confirmed' | 'rejected';

// Impact / Priority 1–5 (Final Implementation Authority §10.4, D-09): default 3.
export type FindingPriority = 1 | 2 | 3 | 4 | 5;

export const FINDING_PRIORITY_ORDER: readonly FindingPriority[] = Object.freeze([1, 2, 3, 4, 5]);

export const DEFAULT_FINDING_PRIORITY: FindingPriority = 3;

export function isFindingPriority(value: unknown): value is FindingPriority {
  return typeof value === 'number' && (FINDING_PRIORITY_ORDER as readonly number[]).includes(value);
}

export interface V2Finding {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  areaId: string;
  origin: FindingOrigin;
  title: string;
  observation: string;
  recommendation: string;
  // Responsible Trade = the D-08 closed set ("other" carries its text in responsibleTradeOther);
  // Report Section = a plain string (pre-filled with the area kind); priority = D-09.
  responsibleTrade: ResponsibleTrade | null;
  responsibleTradeOther: string;
  reportSection: string;
  priority: FindingPriority;
  findingClass: FindingClass;
  status: FindingStatus;
  // NY F1 Round 5 (Owner ruling OD-13, 2026-09-09): a VOLUNTARY OBSERVATION — something the New York
  // Standards of Practice do not require, looked at at the inspector's own discretion and recorded
  // under Other Findings (agreement v2 §3.5). It prints in its own report block, never among the
  // findings of the area, and is never counted as an observed component.
  voluntaryObservation: boolean;
  // 2026-09-13 (ratification §4): the inspector confirmed movement / displacement / significant cracking /
  // deformation / suspected instability / a load-bearing concern → the referral is a Professional Engineer (PE).
  structuralConcern: boolean;
  evidenceIds: string[];
  createdAt: number;
  updatedAt: number;
}

export const FINDING_CLASS_LABELS: Record<FindingClass, string> = {
  defect: '缺陷',
  safety: '安全隐患',
  maintenance: '维护事项',
  advisory: '建议',
  informational: '说明'
};

export const FINDING_CLASS_ORDER: readonly FindingClass[] = Object.freeze(['defect', 'safety', 'maintenance', 'advisory', 'informational']);

export const FINDING_STATUS_LABELS: Record<FindingStatus, string> = {
  draft: '草稿',
  confirmed: '已确认',
  rejected: '已撤回'
};

export function isFindingClass(value: unknown): value is FindingClass {
  return typeof value === 'string' && (FINDING_CLASS_ORDER as readonly string[]).includes(value);
}

export interface NewFindingInput {
  inspectionId: string;
  areaId: string;
  title: string;
  observation?: string;
  recommendation?: string;
  findingClass?: FindingClass;
  evidenceIds?: string[];
  responsibleTrade?: ResponsibleTrade | null;
  responsibleTradeOther?: string;
  reportSection?: string;
  priority?: FindingPriority;
  // 'draft' = local autosaved draft (crash protection; NOT in the Report Draft) · 'confirmed' =
  // explicitly saved by the inspector (ONE FINDING SAVE MODEL, D-15). Default confirmed.
  status?: Extract<FindingStatus, 'draft' | 'confirmed'>;
  // 'ai' when the text started as an AI observation the inspector recorded as an other finding. The
  // inspector's saving is what makes it a finding; the origin is provenance only.
  origin?: FindingOrigin;
  // OD-13: recorded at the inspector's discretion, outside the required scope.
  voluntaryObservation?: boolean;
  structuralConcern?: boolean;
}

// Inspector-authored findings are confirmed by definition — the inspector IS the authority. A draft
// is the same record before the explicit Save Finding commit.
export function createInspectorFinding(input: NewFindingInput, now: number): V2Finding {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('fnd'),
    inspectionId: input.inspectionId,
    areaId: input.areaId,
    origin: input.origin ?? 'inspector',
    title: input.title.trim(),
    observation: (input.observation ?? '').trim(),
    recommendation: (input.recommendation ?? '').trim(),
    responsibleTrade: input.responsibleTrade ?? null,
    responsibleTradeOther: (input.responsibleTradeOther ?? '').trim(),
    reportSection: (input.reportSection ?? '').trim(),
    priority: input.priority ?? DEFAULT_FINDING_PRIORITY,
    findingClass: input.findingClass ?? 'defect',
    status: input.status ?? 'confirmed',
    voluntaryObservation: input.voluntaryObservation === true,
    structuralConcern: input.structuralConcern === true,
    evidenceIds: [...new Set(input.evidenceIds ?? [])],
    createdAt: now,
    updatedAt: now
  };
}

export interface FindingEdit {
  title?: string;
  observation?: string;
  recommendation?: string;
  findingClass?: FindingClass;
  evidenceIds?: string[];
  responsibleTrade?: ResponsibleTrade | null;
  responsibleTradeOther?: string;
  reportSection?: string;
  priority?: FindingPriority;
  voluntaryObservation?: boolean;
  structuralConcern?: boolean;
}

export function withFindingEdit(finding: V2Finding, edit: FindingEdit, now: number): V2Finding {
  return {
    ...finding,
    structuralConcern: edit.structuralConcern !== undefined ? edit.structuralConcern : finding.structuralConcern,
    voluntaryObservation: edit.voluntaryObservation !== undefined ? edit.voluntaryObservation : finding.voluntaryObservation,
    title: edit.title !== undefined ? edit.title.trim() : finding.title,
    observation: edit.observation !== undefined ? edit.observation.trim() : finding.observation,
    recommendation: edit.recommendation !== undefined ? edit.recommendation.trim() : finding.recommendation,
    findingClass: edit.findingClass ?? finding.findingClass,
    evidenceIds: edit.evidenceIds !== undefined ? [...new Set(edit.evidenceIds)] : finding.evidenceIds,
    responsibleTrade: edit.responsibleTrade !== undefined ? edit.responsibleTrade : finding.responsibleTrade,
    responsibleTradeOther: edit.responsibleTradeOther !== undefined ? edit.responsibleTradeOther.trim() : finding.responsibleTradeOther,
    reportSection: edit.reportSection !== undefined ? edit.reportSection.trim() : finding.reportSection,
    priority: edit.priority ?? finding.priority,
    updatedAt: now
  };
}

// Status changes are free in every direction: a rejected finding can be restored, a confirmed
// one withdrawn. Nothing is ever locked (OWNER-CONTRACT §4.6).
export function withFindingStatus(finding: V2Finding, status: FindingStatus, now: number): V2Finding {
  return { ...finding, status, updatedAt: now };
}

export function withFindingEvidenceLink(finding: V2Finding, evidenceId: string, now: number): V2Finding {
  if (finding.evidenceIds.includes(evidenceId)) return finding;
  return { ...finding, evidenceIds: [...finding.evidenceIds, evidenceId], updatedAt: now };
}

export function withoutFindingEvidenceLink(finding: V2Finding, evidenceId: string, now: number): V2Finding {
  if (!finding.evidenceIds.includes(evidenceId)) return finding;
  return { ...finding, evidenceIds: finding.evidenceIds.filter((id) => id !== evidenceId), updatedAt: now };
}

export function isReportableFinding(finding: V2Finding): boolean {
  return finding.status === 'confirmed';
}

// A finding "has content" when the inspector wrote or attached anything; an empty autosaved draft is
// cleared silently.
export function findingHasContent(finding: Pick<V2Finding, 'title' | 'observation' | 'recommendation' | 'evidenceIds'>): boolean {
  return finding.title.trim().length > 0 || finding.observation.trim().length > 0 || finding.recommendation.trim().length > 0 || finding.evidenceIds.length > 0;
}

// D-16 two-level completeness. Level 1 (Save Finding): title + observation. Level 2 (export of a
// Defect finding): recommendation + responsible trade (+ its text when Other) + report section.
export function findingCanSave(finding: Pick<V2Finding, 'title' | 'observation'>): boolean {
  return finding.title.trim().length > 0 && finding.observation.trim().length > 0;
}

export function findingExportIncomplete(
  finding: Pick<V2Finding, 'findingClass' | 'recommendation' | 'responsibleTrade' | 'responsibleTradeOther' | 'reportSection'>
): boolean {
  if (finding.findingClass !== 'defect') return false;
  if (finding.recommendation.trim().length === 0) return true;
  if (finding.responsibleTrade === null) return true;
  if (finding.responsibleTrade === 'other' && finding.responsibleTradeOther.trim().length === 0) return true;
  return finding.reportSection.trim().length === 0;
}
